"use strict";
/*
 * Copyright 2024, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Interview = void 0;
const omit_1 = __importDefault(require("lodash/omit"));
const Uuidable_1 = require("../Uuidable");
const ConstructorUtils_1 = require("../../../utils/ConstructorUtils");
const InterviewParadata_1 = require("./InterviewParadata");
const SurveyObjectUnserializer_1 = require("../SurveyObjectUnserializer");
/*
We need to set these arrays to be able to split attributes and custom attributes during initialization.
Using keyof do not work because constructor is run-time and keyof is typescript only.
*/
const interviewAttributesNames = [
    '_uuid',
    '_id',
    '_participant_id',
    // reviewing flags
    '_isCompleted',
    '_isQuestionable',
    'loginMethod',
    'accessCode',
    'assignedDate',
    'contactPhoneNumber',
    'helpContactPhoneNumber',
    'contactEmail',
    'helpContactEmail',
    'acceptToBeContactedForHelp',
    'wouldLikeToParticipateInOtherSurveys',
    'respondentComments',
    'interviewerComments',
    'auditorComments',
    'durationRange',
    'durationRespondentEstimationMin',
    'interestRange',
    'difficultyRange',
    'burdenRange',
    'consideredAbandoning',
    'preData'
];
const interviewAttributesWithComposedAttributes = [...interviewAttributesNames, '_paradata'];
/**
 * Represents an interview in the survey.
 *
 * @class
 * @description
 * An interview can be associated with an household, a person or an organization. TODO: should we also accept a Vehicle interview?
 *
 * The Interview class is central to the survey process, handling data throughout various stages:
 *
 * 1. Interview Step:
 *    - The Interview object is progressively filled with respondent response.
 *    - Paradata is collected throughout the interview process.
 *    - Both interview data and paradata (interview metadata) are stored in the Interview instance.
 *
 * 2a. Audit Step:
 *    - When an admin/auditor person opens the auditing dashboard:
 *      a. The interview is loaded in auditing mode. This involves cloning the interview
 *         object to keep the original response intact. The copy has isAuditingVersion
 *         flag set to true in the db. This copy will includes any change made by the admin/auditor person.
 *         TODO: consider using db views to separate interviews based on the isAuditingVersion flag.
 *      b. An automatic auditing/enhancement process runs on the backend.
 *      c. Audit results are saved with the interview, creating an InterviewAudited object.
 *      d. The InterviewAudited object is sent back to the frontend.
 *    - The admin/auditing dashboard displays:
 *      a. A summary of the interview and its paradata.
 *      b. Any audits added during the auditing process (if any).
 *
 * 2b. Enhancement Step (done at the same time as the audit):
 *    - During the enhancement process:
 *      a. New attributes are generated as composites of existing attributes.
 *      b. Interview durations and other metadata statistics are calculated.
 *    - These enhancements prepare the interview data for export.
 *
 * 3. Weighting Step:
 *    - Note: The Interview class itself is not involved in weighting.
 *    - Weighting is applied to other objects like Household, Person, Trip, etc.
 *    - This process adjusts object weights to match the reference population.
 *
 * 4. Exporting Step:
 *    - After audit, enhancement and weighting:
 *      a. The export process is initiated from the admin frontend.
 *      b. InterviewAudited data, along with related objects, is prepared for export.
 *
 * Some naming conventions:
 * - Audit: done after the interview is completed or abandoned by the respondent
 * - Validation: done real-time during the interview itself when filled by the respondent or an interviewer
 *
 * This class primarily handles data for step 1 (alongside InterviewParadata).
 * The InterviewAudited class handles data for steps 2, 3 and 4.
 */
class Interview extends Uuidable_1.Uuidable {
    // Use InterviewUnserializer create function to generate/validate Interview object from json data with nested composed objects
    constructor(params, interviewAttributes, surveyObjectsRegistry) {
        super(interviewAttributes.uuid || params._uuid);
        this._surveyObjectsRegistry = surveyObjectsRegistry;
        this._attributes = {};
        this._customAttributes = {};
        const { attributes, customAttributes } = ConstructorUtils_1.ConstructorUtils.initializeAttributes((0, omit_1.default)(params, [
            '_paradata',
            'paradata',
            '_language', // TODO: remove this once we have migrated all interviews to the new paradata structure
            '_languages', // TODO: remove this once we have migrated all interviews to the new paradata structure
            '_browser', // TODO use an array instead of a single browser in survey
            '_startedAt',
            '_updatedAt',
            '_completedAt',
            '_source',
            '_personRandomSequence',
            '_sections'
        ]), interviewAttributesNames, interviewAttributesWithComposedAttributes);
        this._attributes = attributes;
        this._attributes._id = interviewAttributes.id;
        this._attributes._participant_id = interviewAttributes.participant_id;
        this._attributes._isCompleted = interviewAttributes.is_completed;
        this._attributes._isQuestionable = interviewAttributes.is_questionable;
        this._attributes.loginMethod = interviewAttributes.loginMethod;
        this._customAttributes = customAttributes;
        this._paradata = ConstructorUtils_1.ConstructorUtils.initializeComposedAttribute(params._paradata === undefined ? Interview.extractDirtyParadataParams(params) : params._paradata, (_paradataParams) => InterviewParadata_1.InterviewParadata.unserialize(_paradataParams, this._surveyObjectsRegistry), this._surveyObjectsRegistry);
        this._surveyObjectsRegistry.registerInterview(this);
    }
    get attributes() {
        return this._attributes;
    }
    get customAttributes() {
        return this._customAttributes;
    }
    // Getters and setters for the attributes dictionary:
    get id() {
        // no setter, comes from the db
        return this._attributes._id;
    }
    get participant_id() {
        // no setter, comes from the db
        return this._attributes._participant_id;
    }
    get loginMethod() {
        return this._attributes.loginMethod;
    }
    set loginMethod(value) {
        this._attributes.loginMethod = value;
    }
    get isCompleted() {
        return this._attributes._isCompleted;
    }
    set isCompleted(value) {
        this._attributes._isCompleted = value;
    }
    get isQuestionable() {
        return this._attributes._isQuestionable;
    }
    set isQuestionable(value) {
        this._attributes._isQuestionable = value;
    }
    get accessCode() {
        return this._attributes.accessCode;
    }
    set accessCode(value) {
        this._attributes.accessCode = value;
    }
    get assignedDate() {
        return this._attributes.assignedDate;
    }
    set assignedDate(value) {
        this._attributes.assignedDate = value;
    }
    get contactEmail() {
        return this._attributes.contactEmail;
    }
    set contactEmail(value) {
        this._attributes.contactEmail = value;
    }
    get helpContactEmail() {
        return this._attributes.helpContactEmail;
    }
    set helpContactEmail(value) {
        this._attributes.helpContactEmail = value;
    }
    get contactPhoneNumber() {
        return this._attributes.contactPhoneNumber;
    }
    set contactPhoneNumber(value) {
        this._attributes.contactPhoneNumber = value;
    }
    get helpContactPhoneNumber() {
        return this._attributes.helpContactPhoneNumber;
    }
    set helpContactPhoneNumber(value) {
        this._attributes.helpContactPhoneNumber = value;
    }
    get acceptToBeContactedForHelp() {
        return this._attributes.acceptToBeContactedForHelp;
    }
    set acceptToBeContactedForHelp(value) {
        this._attributes.acceptToBeContactedForHelp = value;
    }
    get wouldLikeToParticipateInOtherSurveys() {
        return this._attributes.wouldLikeToParticipateInOtherSurveys;
    }
    set wouldLikeToParticipateInOtherSurveys(value) {
        this._attributes.wouldLikeToParticipateInOtherSurveys = value;
    }
    get respondentComments() {
        return this._attributes.respondentComments;
    }
    set respondentComments(value) {
        this._attributes.respondentComments = value;
    }
    get interviewerComments() {
        return this._attributes.interviewerComments;
    }
    set interviewerComments(value) {
        this._attributes.interviewerComments = value;
    }
    get auditorComments() {
        return this._attributes.auditorComments;
    }
    set auditorComments(value) {
        this._attributes.auditorComments = value;
    }
    get durationRange() {
        return this._attributes.durationRange;
    }
    set durationRange(value) {
        this._attributes.durationRange = value;
    }
    get durationRespondentEstimationMin() {
        return this._attributes.durationRespondentEstimationMin;
    }
    set durationRespondentEstimationMin(value) {
        this._attributes.durationRespondentEstimationMin = value;
    }
    get interestRange() {
        return this._attributes.interestRange;
    }
    set interestRange(value) {
        this._attributes.interestRange = value;
    }
    get difficultyRange() {
        return this._attributes.difficultyRange;
    }
    set difficultyRange(value) {
        this._attributes.difficultyRange = value;
    }
    get burdenRange() {
        return this._attributes.burdenRange;
    }
    set burdenRange(value) {
        this._attributes.burdenRange = value;
    }
    get consideredAbandoning() {
        return this._attributes.consideredAbandoning;
    }
    set consideredAbandoning(value) {
        this._attributes.consideredAbandoning = value;
    }
    get preData() {
        return this._attributes.preData;
    }
    set preData(value) {
        this._attributes.preData = value;
    }
    get paradata() {
        return this._paradata;
    }
    set paradata(value) {
        this._paradata = value;
    }
    /**
     * Creates an Interview object from sanitized parameters
     * This method is used for unserialization when the Interview object comes from serialized data
     * @param {ExtendedInterviewAttributesWithComposedObjects | SerializedExtendedInterviewAttributesWithComposedObjects} params - Sanitized interview parameters
     * @param {rawInterviewAttributes} interviewAttributes - Raw interview attributes from database
     * @returns {Interview} New Interview instance
     */
    static unserialize(params, surveyObjectsRegistry, interviewAttributes) {
        // If we have serialized data, flatten it first
        const flattenedParams = SurveyObjectUnserializer_1.SurveyObjectUnserializer.flattenSerializedData(params);
        // If interviewAttributes are not provided, extract them from the flattened params
        let rawInterviewAttributes;
        if (interviewAttributes) {
            rawInterviewAttributes = interviewAttributes;
        }
        else {
            // Extract the raw interview attributes from the flattened params
            const flattenedParamsAny = flattenedParams;
            rawInterviewAttributes = {
                id: flattenedParamsAny._id,
                uuid: flattenedParamsAny._uuid,
                participant_id: flattenedParamsAny._participant_id,
                is_completed: flattenedParamsAny._isCompleted,
                response: flattenedParamsAny.response || {},
                validations: flattenedParamsAny.validations || {},
                is_questionable: flattenedParamsAny._isQuestionable,
                loginMethod: flattenedParamsAny.loginMethod
            };
        }
        return new Interview(flattenedParams, rawInterviewAttributes, surveyObjectsRegistry);
    }
    /**
     * Checks if the interview has minimum data (startedAt)
     * @returns boolean indicating if the interview has minimum data
     */
    hasMinimumRequiredData() {
        var _a;
        return ((_a = this.paradata) === null || _a === void 0 ? void 0 : _a.startedAt) !== undefined;
    }
    /**
     * Extract paradata params from dirty params
     * @param dirtyParams the dirty params (not validated)
     * @returns the paradata params (not validated)
     */
    static extractDirtyParadataParams(dirtyParams) {
        return {
            languages: dirtyParams._language ? [{ language: dirtyParams._language }] : undefined,
            browsers: dirtyParams._browser ? [dirtyParams._browser] : undefined,
            startedAt: dirtyParams._startedAt,
            updatedAt: dirtyParams._updatedAt,
            completedAt: dirtyParams._completedAt,
            source: dirtyParams._source,
            personsRandomSequence: dirtyParams._personRandomSequence, // TODO: rename to personsRandomSequence in survey
            sections: dirtyParams._sections
        };
    }
}
exports.Interview = Interview;
Interview._confidentialAttributes = [
    // do not export these attributes except for admins
    '_id', // only the uuid should be exported
    '_participant_id',
    'accessCode', // only for admin exports
    'contactPhoneNumber', // only for admin exports
    'helpContactPhoneNumber', // only for admin exports
    'contactEmail', // only for admin exports
    'helpContactEmail', // only for admin exports
    'acceptToBeContactedForHelp', // only for analysis/admin exports
    'wouldLikeToParticipateInOtherSurveys', // only for analysis/admin exports
    'respondentComments', // only for admin exports
    'interviewerComments', // only for admin exports
    'auditorComments', // only for admin exports
    'durationRange', // only for analysis/admin exports
    'durationRespondentEstimationMin', // only for analysis/admin exports
    'interestRange', // only for analysis/admin exports
    'difficultyRange', // only for analysis/admin exports
    'burdenRange', // only for analysis/admin exports
    'consideredAbandoning', // only for analysis/admin exports
    'preData' // only for admin exports
];
//# sourceMappingURL=Interview.js.map